import React, { useState, useMemo } from 'react';
import { XIcon, SearchIcon } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@components/ui/avatar';
import {
  Drawer,
  DrawerContent,
  DrawerClose,
  DrawerTitle,
  DrawerDescription,
} from '@components/ui/drawer';
import { AnimatedTabs } from '@components/ui/animated-tabs';
import type { SpaceIndicatorItem } from '@components/SpaceIndicator';
import { searchKey } from '@utils/string.utils';

interface SpacesSheetProps {
  items: SpaceIndicatorItem[];
  open: boolean;
  onClose: () => void;
  onItemClick: (item: SpaceIndicatorItem) => void;
}

type FilterTab = 'all' | 'communities' | 'events';

const ACTIVE_TAB_STYLE = {
  activeBgClassName: 'bg-mb-teal',
  activeTextClassName: 'text-mb-ink',
} as const;

const TAB_OPTIONS = [
  { value: 'all', label: 'All', ...ACTIVE_TAB_STYLE },
  { value: 'communities', label: 'Communities', ...ACTIVE_TAB_STYLE },
  { value: 'events', label: 'Events', ...ACTIVE_TAB_STYLE },
];

export const SpacesSheet: React.FC<SpacesSheetProps> = ({ items, open, onClose, onItemClick }) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');

  // One search key for every Space picker (searchKey, TECH-718), on the term and on each name. A
  // term of only spaces is no search: the tabs stay and the list stays whole.
  const searchTerm = searchKey(search);
  const isSearching = searchTerm !== '';
  const filtered = useMemo(
    () =>
      items.filter(item => {
        if (isSearching) return searchKey(item.name).includes(searchTerm);
        return (
          activeTab === 'all' ||
          (activeTab === 'communities' && item.type === 'space') ||
          (activeTab === 'events' && item.type === 'event')
        );
      }),
    [items, searchTerm, isSearching, activeTab],
  );

  const spaces = filtered.filter(i => i.type === 'space');
  const events = filtered.filter(i => i.type === 'event');

  const hasSpaces = items.some(i => i.type === 'space');
  const hasEvents = items.some(i => i.type === 'event');

  return (
    <Drawer
      open={open}
      onOpenChange={v => {
        if (!v) {
          setSearch('');
          setActiveTab('all');
          onClose();
        }
      }}
      shouldScaleBackground={false}
    >
      <DrawerContent hideHandle className="max-h-[85dvh] flex flex-col bg-white rounded-t-3xl">
        {/* Handle */}
        <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-gray-200 shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 shrink-0">
          <DrawerTitle className="text-lg font-bold text-gray-900">Spaces</DrawerTitle>
          <DrawerDescription className="sr-only">
            Browse and navigate to spaces and events
          </DrawerDescription>
          <DrawerClose asChild>
            <button
              type="button"
              className="w-10 h-10 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </DrawerClose>
        </div>

        {/* Search */}
        <div className="px-5 pb-3 shrink-0">
          <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2.5">
            <SearchIcon className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="search"
              aria-label="Search spaces and events"
              placeholder="Search spaces..."
              value={search}
              onChange={e => {
                setSearch(e.target.value);
              }}
              className="flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 outline-none"
            />
          </div>
        </div>

        {/* Tabs — hidden during search */}
        {!isSearching && (
          <div className="px-5 pb-3 shrink-0">
            <AnimatedTabs
              value={activeTab}
              onValueChange={v => {
                setActiveTab(v as FilterTab);
              }}
              options={TAB_OPTIONS.filter(
                t =>
                  t.value === 'all' ||
                  (t.value === 'communities' && hasSpaces) ||
                  (t.value === 'events' && hasEvents),
              )}
            />
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-2 min-h-0">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-mb-gray-text py-8">No results found</p>
          ) : (
            <>
              {spaces.length > 0 && (
                <div>
                  {(activeTab === 'all' || isSearching) && <SectionLabel label="Communities" />}
                  {spaces.map(item => (
                    <SpaceRow
                      key={item.id}
                      item={item}
                      onClick={() => {
                        onItemClick(item);
                        onClose();
                      }}
                    />
                  ))}
                </div>
              )}

              {events.length > 0 && (
                <div>
                  {(activeTab === 'all' || isSearching) && <SectionLabel label="Events" />}
                  {events.map(item => (
                    <SpaceRow
                      key={item.id}
                      item={item}
                      onClick={() => {
                        onItemClick(item);
                        onClose();
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 pt-3 pb-safe-or-5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-mb-teal text-mb-ink font-semibold text-base rounded-full py-3.5 hover:bg-mb-teal-dark transition-colors"
          >
            Done
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

// On a short sheet the label sits closer to its rows (TECH-718), as in the composer's picker and the
// share drawer, so a whole first row shows.
const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-2 py-2 px-1 mt-2 [@media(max-height:30rem)]:mt-0 [@media(max-height:30rem)]:py-1">
    <div className="flex-1 h-px bg-gray-200" />
    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{label}</span>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

interface SpaceRowProps {
  item: SpaceIndicatorItem;
  onClick: () => void;
}

const SpaceRow: React.FC<SpaceRowProps> = ({ item, onClick }) => (
  <button
    type="button"
    className="w-full flex items-start gap-3 py-3 text-left hover:bg-gray-50 rounded-xl px-1 transition-colors"
    onClick={onClick}
  >
    {/* The name is the row's text, so the avatar adds nothing to what the row is called: the
        image has alt="" and the initial standing in for it is hidden, as in every Space picker. */}
    <Avatar className="w-11 h-11 shrink-0 rounded-xl">
      <AvatarImage
        src={item.image_url ?? undefined}
        alt=""
        crossOrigin="anonymous"
        className="object-cover"
      />
      <AvatarFallback
        aria-hidden="true"
        className="rounded-xl bg-neutral-200 text-neutral-600 text-sm font-medium"
      >
        {item.name.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
    {/* Top-aligned (items-start), as in every Space picker: a name can wrap, and the avatar stays
        with its first line; the column is the avatar's height at least and centres a one-line
        name on it. The whole name, wrapped (TECH-718): chapters of one community differ only at
        the end. dir="auto": a right-to-left name sets its own direction. */}
    <span className="flex min-h-11 min-w-0 flex-1 flex-col justify-center">
      <span dir="auto" className="wrap-break-word font-medium text-sm text-gray-900">
        {item.name}
      </span>
    </span>
  </button>
);
